import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { isAdminAuthenticated } from '@/lib/admin-auth';
import { PageSeo } from '@/components/seo/PageSeo';
import { SiteIdentityHead } from '@/components/help-center/SiteIdentityHead';
import { PublicToaster } from '@/components/help-center/PublicToaster';
import { CookieConsentBanner } from '@/components/help-center/CookieConsentBanner';
import { COMING_SOON_COOKIE_NAME } from '@/lib/coming-soon';
import { getComingSoonConfig } from '@/services/coming-soon.service';

const BYPASS_PREFIXES = ['/coming-soon', '/admin/login', '/painel', '/api'];
/** Evita ecrã branco indefinido se a API de coming-soon pendurar. */
const COMING_SOON_RESOLVE_MS = 2500;

function shouldBypass(pathname: string): boolean {
  return BYPASS_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix));
}

function readCookieBypass(): boolean | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${COMING_SOON_COOKIE_NAME}=([^;]*)`));
  if (!match) return null;
  if (match[1] === '1') return true;
  if (match[1] === '0') return false;
  return null;
}

export function ComingSoonGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [active, setActive] = useState<boolean | null>(null);
  const [panelAuthenticated, setPanelAuthenticated] = useState(() => isAdminAuthenticated());

  useEffect(() => {
    const onAuthChange = () => setPanelAuthenticated(isAdminAuthenticated());
    window.addEventListener('xat-auth-changed', onAuthChange);
    return () => window.removeEventListener('xat-auth-changed', onAuthChange);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (isAdminAuthenticated()) {
        if (!cancelled) setActive(false);
        return;
      }

      if (import.meta.env.VITE_COMING_SOON_ENABLED === 'true') {
        if (!cancelled) setActive(true);
        return;
      }

      const cookie = readCookieBypass();

      const configPromise = getComingSoonConfig();
      const timeoutPromise = new Promise<null>((resolve) => {
        window.setTimeout(() => resolve(null), COMING_SOON_RESOLVE_MS);
      });

      const config = await Promise.race([configPromise, timeoutPromise]);
      if (cancelled) return;

      if (config !== null) {
        setActive(config.enabled);
        return;
      }

      // Timeout / falha de rede: cookie local, senão deixa o site público abrir.
      setActive(cookie ?? false);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [panelAuthenticated]);

  if (active === null) {
    return (
      <div
        className="min-h-screen w-full bg-[#030023]"
        aria-busy="true"
        aria-label="A carregar"
      />
    );
  }

  if (active && !panelAuthenticated && !shouldBypass(location.pathname)) {
    return <Navigate to="/coming-soon" replace />;
  }

  return <>{children}</>;
}

export function PublicLayout() {
  return (
    <ComingSoonGuard>
      <PageSeo />
      <SiteIdentityHead />
      <Outlet />
      <CookieConsentBanner />
      <PublicToaster />
    </ComingSoonGuard>
  );
}
